<template>
  <div class="thread-header">
    <toolbar fixed :logo="false" class="thread__header">
      <template v-slot:left>
        <div class="header-main d-flex align-items-center ml-1">
          <template v-if="threadUsers.length">
            <subscriber-avatars
              :show-remaining-number="false"
              :subscribers="threadUsers"
              :limit="3"
              :small="threadUsers.length === 1"
              :x-small="threadUsers.length > 1"
              :default-text="title"
              class="mr-1"
            />
          </template>
          <template
            v-else-if="
              threadParentMessage && messageThread && messageThread.participants
            "
          >
            <subscriber-avatars
              :subscribers="messageThread.participants.map(p => p.user)"
              x-small
              class="mr-1"
            />
          </template>
          <template v-else-if="threadParentMessage && !messageThread">
            <div class="header-icon">
              <subscriber-avatar
                :subscriber="threadParentMessage.user"
                :default-text="title"
              />
            </div>
          </template>
          <template v-else>
            <div class="header-icon">
              <v-icon icon="forum" size="1.3rem" />
            </div>
          </template>
          <div class="header-info d-flex flex-column">
            <div class="chat-name">
              {{ title }}
            </div>
            <div v-if="timestampAgo" class="chat-timestamp-ago">
              {{ timestampAgo }}
            </div>
          </div>
        </div>
      </template>

      <template v-slot:right>
        <div
          id="page-toolbar-actions"
          class="thread-actions d-flex align-items-center"
        />
      </template>

      <template v-slot:append>
        <div class="container">
          <div v-if="shouldShowParentPage" class="pb-2 px-2">
            <v-button small outlined block :to="threadParentPage.path">
              {{ $t("components.messageThread.header.viewFullDiscussion") }}
              <v-icon icon="arrow_forwards" class="ml-1" />
            </v-button>
          </div>
        </div>
      </template>
    </toolbar>

    <div
      v-if="threadParentMessage && threadParentMessage.message_thread_id"
      :class="[
        { 'bg-white': !isParentMessageFeed, '--feed': isParentMessageFeed },
        'thread-header__parent-message-wrapper'
      ]"
    >
      <div class="container">
        <div
          :class="[
            shouldShowParentPage ? 'pt-5 mt-2' : 'pt-1',
            !isParentMessageFeed ? 'px-2 pb-2' : ''
          ]"
        >
          <message
            :message-thread-id="threadParentMessage.message_thread_id"
            :message="threadParentMessage"
            :is-feed="isParentMessageFeed"
            :hide-replies="!isParentMessageFeed"
            is-last
            is-first
            @react="data => handleReact(data.action, data.message)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from "vuex"

import { isPresent } from "@/lib/utils"
import { shownParticipants } from "@/lib/messages-utils"

import Toolbar from "@/components/Toolbar"

export default {
  components: {
    Toolbar,
    Message: () => import("@/components/message-thread/Message"),
    SubscriberAvatar: () => import("@/components/SubscriberAvatar"),
    SubscriberAvatars: () => import("@/components/SubscriberAvatars")
  },

  props: {
    users: { type: Array, default: () => [] },
    messageThread: { type: Object, default: () => {} },
    parentMessage: { type: Object, default: () => {} },
    timestampAgo: { type: String, default: null }
  },

  computed: {
    ...mapState("analytics", ["sessionId"]),
    ...mapGetters("messages", ["getMessage"]),
    ...mapGetters("auth", ["isAuthenticated", "currentUser"]),

    title() {
      if (this.parentMessage || this.messageThread?.parent_message) {
        return "Thread"
      } else {
        return (
          this.messageThread?.title ||
          this.threadUsers
            .map(s => s.name)
            .filter(Boolean)
            .join(", ")
        )
      }
    },

    threadUsers() {
      if (this.users.length) {
        return this.users
      } else {
        return shownParticipants(this.messageThread, this.currentUser).map(
          p => p.user
        )
      }
    },

    shouldShowParentPage() {
      return !this.$route.query.reply && this.threadParentPage
    },

    threadParentPage() {
      return this.messageThread && this.messageThread?.parent_message
        ? this.messageThread.parent_message.page
        : null
    },

    threadParentMessage() {
      return this.messageThread && this.messageThread?.parent_message
        ? this.getMessage(
            this.messageThread.parent_message.message_thread_id,
            this.messageThread.parent_message.message_ts
          ) || this.messageThread.parent_message
        : this.parentMessage
        ? this.parentMessage
        : {}
    },

    isParentMessageFeed() {
      return !!this.messageThread?.parent_message?.message_thread?.is_feed
    }
  },

  mounted() {
    if (
      this.threadParentMessage &&
      this.threadParentMessage.message_thread_id
    ) {
      this.subscribeToThread({
        subscriptionId: `thread-header-${this.threadParentMessage.message_thread_id}`,
        messageThreadId: this.threadParentMessage.message_thread_id
      })
    }
  },

  methods: {
    isPresent,

    ...mapActions("messages", ["reactMessage", "subscribeToThread"]),

    handleReact(action, message) {
      if (this.isAuthenticated) {
        this.reactMessage({
          message,
          action,
          likedUserId: this.currentUser?.id,
          sessionId: this.sessionId
        })
      } else {
        this.$router.push("#auth")
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

// Back button width + subscriber avatars width + right buttons width
$thread-header-control--width: calc(
  $size--10 + $size--8 * 2 + $notification-button--size * 2
);

$thread-header-name--mobile-width: calc(100vw - $thread-header-control--width);
$thread-header-name--desktop-width: calc(
  $content--max-width - $thread-header-control--width
);

$thread-header-name--width: min(
  $thread-header-name--mobile-width,
  $thread-header-name--desktop-width
);

$icon--size: 40px;

.thread__header {
  min-height: $thread--header-height;

  .header-icon {
    background-color: $color-ui--grey-80;
    display: flex;
    width: $icon--size;
    height: $icon--size;
    margin-right: 10px;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    color: $color-ui--white;
    font-weight: 600;

    &.--private {
      background-color: $color-ui--grey-50;
    }
  }

  .header-main {
    padding: $size--4 0;
  }

  .header-info {
    max-width: $thread-header-name--width;
  }

  .chat-name {
    @include overflow-ellipsis;

    font-size: 16px;
    font-weight: bold;
    color: $color-ui--grey-80;
  }

  .chat-timestamp-ago {
    font-size: 11px;
    color: $color-ui--grey-60;
    font-weight: 600;
  }

  .thread-actions {
    .v-toolbar__button {
      margin: 0 !important;
    }
  }
}

.thread-header__parent-message-wrapper {
  padding-top: env(safe-area-inset-top);

  &.--feed {
    .message {
      padding: $size--3 $size--3 $size--2;
      border: 1px solid $color-ui--grey-20;
      background: $color-ui--white;
      border-top-left-radius: var(--border-radius);
      border-top-right-radius: var(--border-radius);

      .message__wrapper .message__body .body-attachment {
        width: calc(100% + 2rem);
      }
    }
  }
}
</style>
